<template>
	<EventLayout :event="event" :subtitle="stage.title" :return-url="route('events.stages.selection.index', event.id)" :return-label="trans('events.stages.selection.title')">
		<template #actions>
			<PrimaryButton :color="Color.Green" @click.stop="update">
				<i class="fa-solid fa-save mr-2"/>
				{{ trans('general.save') }}
			</PrimaryButton>
		</template>
		<EditStage :stage="stage">
			<div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
				<Card :title="trans('events.stages.selection.enabled_enrollments')">
					<div class="grid grid-cols-1 md:grid-cols-2 gap-3">
						<Switch v-model="stage.early_enabled" full label="events.stages.selection.early_enabled"/>
						<Switch v-model="stage.general_enabled" full label="events.stages.selection.general_enabled"/>
						<Switch v-model="stage.local_enabled" full label="events.stages.selection.local_enabled"/>
						<Switch v-model="stage.international_enabled" full label="events.stages.selection.international_enabled"/>
						<Switch v-model="stage.representative_enabled" full label="events.stages.selection.representative_enabled"/>
						<Switch v-model="stage.organizer_enabled" full label="events.stages.selection.organizer_enabled"/>
						<Switch v-model="stage.workgroup_enabled" full label="events.stages.selection.workgroup_enabled"/>
						<Switch v-model="stage.board_enabled" full label="events.stages.selection.board_enabled"/>
						<Switch v-model="stage.dinosaur_enabled" full label="events.stages.selection.dinosaur_enabled"/>
					</div>
				</Card>
				<Card :title="trans('events.stages.selection.packs.title')" :subtitle="trans('events.stages.selection.packs.subtitle')">
					<LabeledObject>
						<template #label>{{ trans('events.stages.selection.cap') }}</template>
						<NumberInput v-model="stage.cap" :min="0" :max="100"/>
					</LabeledObject>
					<List>
						<ListItem v-for="pack in packs" :title="pack.name" @click.stop="togglePack(pack.id)">
							<i v-if="selectedPackIds.includes(pack.id)" class="fa-solid fa-toggle-on text-green-600"/>
							<i v-else class="fa-solid fa-toggle-off text-red-600"/>
						</ListItem>
					</List>
				</Card>
			</div>
		</EditStage>
	</EventLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import Card from '@/Components/UI/Cards/Card.vue'
import {defineProps, type Ref, ref} from 'vue'
import List from '@/Components/UI/Lists/List.vue'
import {type Event} from '@/Types/Events/Event'
import {type Pack} from '@/Types/Events/Packs/Pack'
import EventLayout from '@/Layouts/EventLayout.vue'
import Switch from '@/Components/UI/Switches/Switch.vue'
import ListItem from '@/Components/UI/Lists/ListItem.vue'
import EditStage from '@/Components/Events/Stages/EditStage.vue'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import {showInfo, showSuccess, showValidationErrors} from '@/Helpers/general'
import {type SelectionStage} from '@/Types/Events/Stages/SelectionStage'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import NumberInput from '@/Components/UI/Inputs/NumberInput.vue'

const props = defineProps<{
	event:Event,
	packs:Array<Pack>,
	stage:SelectionStage,
	selectedPackIds:Array<number>,
}>()

const event:Ref<Event> = ref(props.event)
const working:Ref<boolean> = ref(false)
const packs:Ref<Array<Pack>> = ref(props.packs)
const stage:Ref<SelectionStage> = ref(props.stage)
const selectedPackIds:Ref<Array<number>> = ref(props.selectedPackIds)

function update():void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('events.stages.selection.update', {event: event.value.id, stage: stage.value.id}), stage.value).then(() => {
			showSuccess(trans('general.updated'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function togglePack(packId:number):void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('events.stages.selection.toggle', {event: event.value.id, stage: stage.value.id, pack: packId})).then(() => {
			showSuccess(trans('general.updated'))
			const index:number = selectedPackIds.value.findIndex((id:number) => id === packId)
			if(index > -1){
				selectedPackIds.value.splice(index, 1)
			} else {
				selectedPackIds.value.push(packId)
			}
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>